'use client'

import { GRID } from '@lib/constants'
import { createContext, useContext, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import { getLSTheme, setLSTheme } from '@lib/utils'
import type { PropsWithChildren } from 'react'

interface ThemeProperties {
  updatedDesign: boolean
  darkMode?: boolean
  screenWidth?: number
}

interface PROVIDER_VALUES {
  theme: ThemeProperties
  isDesktopWidth: boolean
  setGlobalTheme: ({ updatedDesign, darkMode }: ThemeProperties) => void
  screenWidth?: number
}

const initialState = {
  updatedDesign: true,
  darkMode: false,
  screenWidth: undefined,
}

const ThemeContext = createContext({})

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [theme, setTheme] = useState<ThemeProperties>(initialState)

  useEffect(() => {
    const lsTheme = getLSTheme()
    if (lsTheme) {
      setTheme((theme) => ({ ...theme, ...lsTheme }))
    } else {
      setLSTheme(initialState)
    }
  }, [])

  useEffect(() => {
    const onResize = () => {
      const { innerWidth } = window
      setTheme((theme) => ({ ...theme, screenWidth: innerWidth }))
    }
    window.addEventListener('resize', debounce(onResize, 200))
    onResize()

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const setGlobalTheme = ({
    updatedDesign,
    darkMode = false,
  }: ThemeProperties) => {
    setTheme({ updatedDesign, darkMode })
  }

  const providerValues: PROVIDER_VALUES = {
    theme,
    isDesktopWidth: Number(theme.screenWidth) > GRID.xl,
    setGlobalTheme,
    screenWidth: theme.screenWidth,
  }

  return (
    <ThemeContext.Provider value={providerValues}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext) as PROVIDER_VALUES
